/**
 * Admin › Finanças › Cockpit de incentivos (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 sections 3.4-3.7) - the pure view-models behind the cockpit's panels, over
 * `@vello/api-client` `IncentiveCockpitAdminApi` / `IncentiveControllerAdminApi` / `ActiveIncentivesAdminApi`:
 *
 *  - the spend panel: the day's committed curve against the controller's pacing plan, and where the market stands against it;
 *  - the zone map: one colour per state of the newest decision, and the offers in force;
 *  - the active incentives list: the source chip, the countdown to the expiry;
 *  - the rule editor: the draft as typed (strings), the controller policy it builds, and the comparison of two previews.
 * Nothing here names a country, a currency or an amount; every code is read through the vocabulary by the screen.
 */
import type {
  CockpitPreview,
  CockpitSpendSide,
  ControllerLever,
  ControllerMode,
  ControllerPolicyDraft,
  ControllerZoneState,
} from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { bpToPercentText, percentTextToBp } from './active-incentives.js';

export type CockpitTone = 'neutral' | 'success' | 'warning' | 'danger';

export function incentiveCockpitAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean; readonly mayApprove: boolean; readonly mayPropose: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true, mayApprove: true, mayPropose: true };
  return {
    mayView: grantsAllow(grants, { permission: 'incentive_lever:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'incentive_lever:manage', target: 'GEO' }),
    mayApprove: grantsAllow(grants, { permission: 'incentive_lever:approve', target: 'GEO' }),
    // The controller policy is a governed key: the server checks `config:propose` at the bucket; the button is only a courtesy.
    mayPropose: grantsAllow(grants, { permission: 'config:propose', target: 'GEO' }),
  };
}

export const COCKPIT_TABS = ['operation', 'zones', 'results', 'rules'] as const;
export type CockpitTab = (typeof COCKPIT_TABS)[number];

// ---- spend curve vs plan ------------------------------------------------------------------------------------------------

const toBig = (amountMinor: string | undefined): bigint | undefined => (amountMinor !== undefined && /^-?\d+$/.test(amountMinor) ? BigInt(amountMinor) : undefined);

/** A wire amount as a plain number for a chart axis (amounts are bounded far below 2^53); undefined when it is not one. */
export function minorToNumber(m: { readonly amountMinor: string } | null | undefined): number | null {
  const big = toBig(m?.amountMinor);
  return big === undefined ? null : Number(big);
}

export interface SpendSeries {
  readonly labels: readonly number[];
  readonly committed: readonly (number | null)[];
  readonly planned: readonly (number | null)[];
}

/** The two curves of one envelope, hour by hour (the committed curve stops at the current hour; no plan = no planned curve). */
export function spendSeries(side: CockpitSpendSide): SpendSeries {
  return {
    labels: side.hours.map((h) => h.hour),
    committed: side.hours.map((h) => minorToNumber(h.committed)),
    planned: side.hours.map((h) => minorToNumber(h.planned)),
  };
}

export type PaceState = 'NO_PLAN' | 'NOT_STARTED' | 'BEHIND' | 'ON_PACE' | 'AHEAD';

export interface Pace {
  readonly state: PaceState;
  /** Committed today over planned until now, basis points (null with no plan or nothing planned yet). */
  readonly ratioBp: number | null;
  readonly plannedNowMinor: bigint | null;
}

/**
 * Where an envelope stands against its plan at the market's wall-clock `minuteOfDay`: the plan interpolated inside the current hour (the controller's own `plannedShare`), the committed total today.
 * AHEAD = at or above the plan (the controller offers nothing more: never ahead of pace); BEHIND = under 70% of it.
 */
export function paceOf(side: CockpitSpendSide, minuteOfDay: number | null): Pace {
  const budget = toBig(side.dayBudget?.amountMinor);
  if (budget === undefined || minuteOfDay === null) return { state: 'NO_PLAN', ratioBp: null, plannedNowMinor: null };
  // The market's WALL-CLOCK minute (the clock the controller paces by): a day is 23 or 25 hours on a DST change, so elapsed time since the midnight would be an hour off.
  const minutes = Math.max(0, Math.min(1_439, Math.floor(minuteOfDay)));
  const hour = Math.floor(minutes / 60);
  const endNow = toBig(side.hours[hour]?.planned?.amountMinor);
  const endBefore = hour === 0 ? 0n : toBig(side.hours[hour - 1]?.planned?.amountMinor);
  if (endNow === undefined || endBefore === undefined) return { state: 'NO_PLAN', ratioBp: null, plannedNowMinor: null };
  const plannedNowMinor = endBefore + ((endNow - endBefore) * BigInt(minutes % 60)) / 60n;
  const committed = toBig(side.committedToday.amountMinor) ?? 0n;
  if (plannedNowMinor <= 0n) return { state: 'NOT_STARTED', ratioBp: null, plannedNowMinor };
  const ratioBp = Number((committed * 10_000n) / plannedNowMinor);
  return { state: ratioBp >= 10_000 ? 'AHEAD' : ratioBp < 7_000 ? 'BEHIND' : 'ON_PACE', ratioBp, plannedNowMinor };
}

/**
 * The caption key of a pace (D865, L6c-F11). "Ahead of plan" used to end with "the controller holds back" whatever the controller's mode: in SHADOW it only observes and logs, and never
 * holds spend back, so the caption says what the mode really does. Every other state reads the same in every mode. A server that sends no mode gets the caption with no claim about the controller.
 */
export function paceMessageKey(state: PaceState, mode: 'OFF' | 'SHADOW' | 'RULES' | undefined): string {
  if (state !== 'AHEAD') return `cpt.pace.${state}`;
  return mode === 'RULES' ? 'cpt.pace.AHEAD' : mode === 'SHADOW' ? 'cpt.pace.AHEAD_SHADOW' : mode === 'OFF' ? 'cpt.pace.AHEAD_OFF' : 'cpt.pace.AHEAD_PLAIN';
}

export const paceTone = (state: PaceState): CockpitTone => (state === 'AHEAD' ? 'warning' : state === 'ON_PACE' ? 'success' : 'neutral');

// ---- zone map -------------------------------------------------------------------------------------------------------------

/** The map colour (a design token) of a zone, by the state of its newest decision. */
export function zoneStateColor(state: ControllerZoneState | undefined): string {
  switch (state) {
    case 'SUPPLY_SHORT':
      return 'var(--color-danger)';
    case 'DEMAND_SHORT':
      return 'var(--color-warning)';
    case 'BALANCED':
      return 'var(--color-success-text)';
    case 'QUIET':
      return 'var(--color-text-tertiary)';
    default:
      return 'var(--color-text-secondary)';
  }
}

export const zoneStateTone = (state: ControllerZoneState | undefined): CockpitTone => (state === 'SUPPLY_SHORT' ? 'danger' : state === 'DEMAND_SHORT' ? 'warning' : state === 'BALANCED' ? 'success' : 'neutral');

// ---- expiry countdown -------------------------------------------------------------------------------------------------------

export interface Countdown {
  /** `ENDED` = past; `SOON` = under five minutes; `LIVE` otherwise. */
  readonly state: 'LIVE' | 'SOON' | 'ENDED' | 'SCHEDULED';
  readonly minutes: number;
}

/** The time left of an offer: whole minutes (rounded up) to its expiry, or to its start when it has not begun. */
export function countdownOf(validFrom: string, validTo: string, nowMs: number): Countdown {
  const from = Date.parse(validFrom);
  const to = Date.parse(validTo);
  if (nowMs < from) return { state: 'SCHEDULED', minutes: Math.ceil((from - nowMs) / 60_000) };
  if (nowMs >= to) return { state: 'ENDED', minutes: 0 };
  const minutes = Math.ceil((to - nowMs) / 60_000);
  return { state: minutes <= 5 ? 'SOON' : 'LIVE', minutes };
}

/** Days, hours and minutes of a duration in minutes as three numbers (the screen words them). */
export function splitMinutes(total: number): { readonly days: number; readonly hours: number; readonly minutes: number } {
  const t = Math.max(0, Math.floor(total));
  return { days: Math.floor(t / 1_440), hours: Math.floor((t % 1_440) / 60), minutes: t % 60 };
}

// ---- rule editor ----------------------------------------------------------------------------------------------------------------

export const SUPPLY_LEVERS: readonly ControllerLever[] = ['DRIVER_BOOST', 'COURIER_BOOST'];
export const DEMAND_LEVERS: readonly ControllerLever[] = ['RIDER_ZONE_DISCOUNT'];
export const EDITOR_MODES: readonly ControllerMode[] = ['OFF', 'SHADOW', 'RULES'];

export interface LeverDraft {
  readonly enabled: boolean;
  /** Discount: the most of a fare (percent). Boost: unused. */
  readonly maxPercent: string;
  /** Discount: the most per ride. Boost: the most per trip. */
  readonly maxPerUnit: bigint | undefined;
  readonly maxSpendCap: bigint | undefined;
  readonly floorPercent: string;
  readonly stepPercent: string;
  readonly cooldownMinutes: string;
}

export interface PolicyEditorDraft {
  readonly mode: ControllerMode;
  readonly minShadowDays: string;
  readonly intervalSeconds: string;
  readonly decisionTtlMinutes: string;
  readonly lookbackWindows: string;
  readonly maxSignalAgeSeconds: string;
  readonly minRequests: string;
  readonly supplyEnterRate: string;
  readonly supplyExitRate: string;
  readonly supplyEnterEta: string;
  readonly supplyExitEta: string;
  readonly demandMinDriversFree: string;
  readonly demandEnterUtil: string;
  readonly demandExitUtil: string;
  readonly demandEnterConv: string;
  readonly demandExitConv: string;
  readonly demandMinQuotes: string;
  readonly quietMaxQuotes: string;
  readonly quietMaxDrivers: string;
  readonly supplyRules: readonly ControllerLever[];
  readonly demandRules: readonly ControllerLever[];
  readonly levers: Readonly<Record<ControllerLever, LeverDraft>>;
  readonly dayBudgetSharePercent: string;
  readonly maxDayBudget: bigint | undefined;
  readonly minScalePercent: string;
  /** `KEEP` the curve of the policy in force (or flat when there is none); `FLAT` an even day. */
  readonly curve: 'KEEP' | 'FLAT';
}

export const FLAT_CURVE: readonly number[] = [...Array<number>(16).fill(417), ...Array<number>(8).fill(416)];

const s = (n: number): string => String(n);
const leverDraftOf = (c: { enabled: boolean; maxSpendCapMinor: number; floorLevelBp: number; maxStepBp: number; cooldownSeconds: number }, maxPercent: string, maxPerUnitMinor: number): LeverDraft => ({
  enabled: c.enabled,
  maxPercent,
  maxPerUnit: BigInt(maxPerUnitMinor),
  maxSpendCap: BigInt(c.maxSpendCapMinor),
  floorPercent: bpToPercentText(c.floorLevelBp),
  stepPercent: bpToPercentText(c.maxStepBp),
  cooldownMinutes: s(Math.round(c.cooldownSeconds / 60)),
});
const blankLever = (enabled: boolean): LeverDraft => ({ enabled, maxPercent: '', maxPerUnit: undefined, maxSpendCap: undefined, floorPercent: '20', stepPercent: '30', cooldownMinutes: '10' });

/** The editor's starting draft: the policy in force as typed text, or a blank SHADOW draft (nothing enabled) when the market publishes none. */
export function draftFromPolicy(value: ControllerPolicyDraft | null): PolicyEditorDraft {
  if (value === null) {
    return {
      mode: 'SHADOW', minShadowDays: '14', intervalSeconds: '120', decisionTtlMinutes: '15', lookbackWindows: '3', maxSignalAgeSeconds: '600', minRequests: '5',
      supplyEnterRate: '20', supplyExitRate: '10', supplyEnterEta: '600', supplyExitEta: '420', demandMinDriversFree: '3', demandEnterUtil: '30', demandExitUtil: '45', demandEnterConv: '40', demandExitConv: '50', demandMinQuotes: '10',
      quietMaxQuotes: '2', quietMaxDrivers: '1', supplyRules: [], demandRules: [],
      levers: { RIDER_ZONE_DISCOUNT: blankLever(false), DRIVER_BOOST: blankLever(false), COURIER_BOOST: blankLever(false) },
      dayBudgetSharePercent: '20', maxDayBudget: undefined, minScalePercent: '10', curve: 'FLAT',
    };
  }
  const t = value.thresholds;
  return {
    mode: value.mode, minShadowDays: s(value.minShadowDays), intervalSeconds: s(value.intervalSeconds), decisionTtlMinutes: s(Math.round(value.decisionTtlSeconds / 60)), lookbackWindows: s(value.lookbackWindows),
    maxSignalAgeSeconds: s(value.maxSignalAgeSeconds), minRequests: s(t.minRequests),
    supplyEnterRate: bpToPercentText(t.supplyShort.enterNoDriverRateBp), supplyExitRate: bpToPercentText(t.supplyShort.exitNoDriverRateBp), supplyEnterEta: s(t.supplyShort.enterEtaSeconds), supplyExitEta: s(t.supplyShort.exitEtaSeconds),
    demandMinDriversFree: s(t.demandShort.minDriversFree), demandEnterUtil: bpToPercentText(t.demandShort.enterUtilisationMaxBp), demandExitUtil: bpToPercentText(t.demandShort.exitUtilisationMaxBp),
    demandEnterConv: bpToPercentText(t.demandShort.enterConversionMaxBp), demandExitConv: bpToPercentText(t.demandShort.exitConversionMaxBp), demandMinQuotes: s(t.demandShort.minQuotes),
    quietMaxQuotes: s(t.quiet.maxQuotes), quietMaxDrivers: s(t.quiet.maxDriversOnline),
    supplyRules: value.rules.supplyShort, demandRules: value.rules.demandShort,
    levers: {
      RIDER_ZONE_DISCOUNT: leverDraftOf(value.levers.RIDER_ZONE_DISCOUNT, bpToPercentText(value.levers.RIDER_ZONE_DISCOUNT.maxDiscountBp), value.levers.RIDER_ZONE_DISCOUNT.maxDiscountPerRideMinor),
      DRIVER_BOOST: leverDraftOf(value.levers.DRIVER_BOOST, '', value.levers.DRIVER_BOOST.maxBoostMinor),
      COURIER_BOOST: leverDraftOf(value.levers.COURIER_BOOST, '', value.levers.COURIER_BOOST.maxBoostMinor),
    },
    dayBudgetSharePercent: bpToPercentText(value.pacing.dayBudgetShareBp), maxDayBudget: BigInt(value.pacing.maxDayBudgetMinor), minScalePercent: bpToPercentText(value.pacing.minScaleBp), curve: 'KEEP',
  };
}

export type EditorIssue = 'NUMBER_INVALID' | 'PERCENT_INVALID' | 'MONEY_REQUIRED' | 'CURRENCY_REQUIRED';

const intOf = (text: string): number | undefined => (/^\d{1,9}$/.test(text.trim()) ? Number(text.trim()) : undefined);
const MAX_SAFE_MINOR = BigInt(Number.MAX_SAFE_INTEGER);

/**
 * The controller policy a draft types, or the first field that is not a number. Only the SHAPE is checked here (a number where a number belongs); the governed key's own validator
 * (rule table, exit beyond enter, caps, the curve) answers through the preview and the proposal, in its stable codes - the editor never re-states those rules.
 */
export function draftToPolicy(draft: PolicyEditorDraft, currency: string | null, keepCurve: readonly number[] | undefined): { readonly ok: true; readonly value: ControllerPolicyDraft } | { readonly ok: false; readonly issue: EditorIssue; readonly field: string } {
  if (currency === null) return { ok: false, issue: 'CURRENCY_REQUIRED', field: 'currency' };
  const bad = (issue: EditorIssue, field: string) => ({ ok: false as const, issue, field });
  const pct = (text: string): number | undefined => percentTextToBp(text);
  const interval = intOf(draft.intervalSeconds);
  const ttl = intOf(draft.decisionTtlMinutes);
  const lookback = intOf(draft.lookbackWindows);
  const age = intOf(draft.maxSignalAgeSeconds);
  const shadow = intOf(draft.minShadowDays);
  const minReq = intOf(draft.minRequests);
  const sEnterEta = intOf(draft.supplyEnterEta);
  const sExitEta = intOf(draft.supplyExitEta);
  const dFree = intOf(draft.demandMinDriversFree);
  const dQuotes = intOf(draft.demandMinQuotes);
  const qQuotes = intOf(draft.quietMaxQuotes);
  const qDrivers = intOf(draft.quietMaxDrivers);
  const checks: readonly [string, number | undefined][] = [
    ['intervalSeconds', interval], ['decisionTtlMinutes', ttl], ['lookbackWindows', lookback], ['maxSignalAgeSeconds', age], ['minShadowDays', shadow], ['minRequests', minReq], ['supplyEnterEta', sEnterEta], ['supplyExitEta', sExitEta],
    ['demandMinDriversFree', dFree], ['demandMinQuotes', dQuotes], ['quietMaxQuotes', qQuotes], ['quietMaxDrivers', qDrivers],
  ];
  for (const [field, n] of checks) if (n === undefined) return bad('NUMBER_INVALID', field);
  const sEnterRate = pct(draft.supplyEnterRate);
  const sExitRate = pct(draft.supplyExitRate);
  const dEnterUtil = pct(draft.demandEnterUtil);
  const dExitUtil = pct(draft.demandExitUtil);
  const dEnterConv = pct(draft.demandEnterConv);
  const dExitConv = pct(draft.demandExitConv);
  const share = pct(draft.dayBudgetSharePercent);
  const minScale = pct(draft.minScalePercent);
  const percents: readonly [string, number | undefined][] = [
    ['supplyEnterRate', sEnterRate], ['supplyExitRate', sExitRate], ['demandEnterUtil', dEnterUtil], ['demandExitUtil', dExitUtil], ['demandEnterConv', dEnterConv], ['demandExitConv', dExitConv], ['dayBudgetSharePercent', share], ['minScalePercent', minScale],
  ];
  for (const [field, n] of percents) if (n === undefined) return bad('PERCENT_INVALID', field);
  if (draft.maxDayBudget === undefined || draft.maxDayBudget > MAX_SAFE_MINOR) return bad('MONEY_REQUIRED', 'maxDayBudget');

  const lever = (name: ControllerLever) => {
    const d = draft.levers[name];
    const floor = pct(d.floorPercent);
    const step = pct(d.stepPercent);
    const cooldown = intOf(d.cooldownMinutes);
    if (floor === undefined || step === undefined) return bad('PERCENT_INVALID', `${name}.${floor === undefined ? 'floorPercent' : 'stepPercent'}`);
    if (cooldown === undefined) return bad('NUMBER_INVALID', `${name}.cooldownMinutes`);
    if (d.maxSpendCap === undefined || d.maxSpendCap > MAX_SAFE_MINOR) return bad('MONEY_REQUIRED', `${name}.maxSpendCap`);
    if (d.maxPerUnit === undefined || d.maxPerUnit > MAX_SAFE_MINOR) return bad('MONEY_REQUIRED', `${name}.maxPerUnit`);
    let maxBp = 0;
    if (name === 'RIDER_ZONE_DISCOUNT') {
      const p = pct(d.maxPercent);
      if (p === undefined) return bad('PERCENT_INVALID', `${name}.maxPercent`);
      maxBp = p;
    }
    return { ok: true as const, common: { enabled: d.enabled, maxSpendCapMinor: Number(d.maxSpendCap), floorLevelBp: floor, maxStepBp: step, cooldownSeconds: cooldown * 60 }, maxBp, perUnit: Number(d.maxPerUnit) };
  };
  const rider = lever('RIDER_ZONE_DISCOUNT');
  if (!rider.ok) return rider;
  const driver = lever('DRIVER_BOOST');
  if (!driver.ok) return driver;
  const courier = lever('COURIER_BOOST');
  if (!courier.ok) return courier;
  const curve = draft.curve === 'KEEP' && keepCurve !== undefined && keepCurve.length === 24 ? keepCurve : FLAT_CURVE;
  return {
    ok: true,
    value: {
      mode: draft.mode, currency, intervalSeconds: interval as number, decisionTtlSeconds: (ttl as number) * 60, lookbackWindows: lookback as number, maxSignalAgeSeconds: age as number, minShadowDays: shadow as number,
      thresholds: {
        minRequests: minReq as number,
        supplyShort: { enterNoDriverRateBp: sEnterRate as number, exitNoDriverRateBp: sExitRate as number, enterEtaSeconds: sEnterEta as number, exitEtaSeconds: sExitEta as number },
        demandShort: {
          minDriversFree: dFree as number, enterUtilisationMaxBp: dEnterUtil as number, exitUtilisationMaxBp: dExitUtil as number, enterConversionMaxBp: dEnterConv as number, exitConversionMaxBp: dExitConv as number, minQuotes: dQuotes as number,
        },
        quiet: { maxQuotes: qQuotes as number, maxDriversOnline: qDrivers as number },
      },
      rules: { supplyShort: draft.supplyRules.filter((l) => SUPPLY_LEVERS.includes(l)), demandShort: draft.demandRules.filter((l) => DEMAND_LEVERS.includes(l)) },
      levers: {
        RIDER_ZONE_DISCOUNT: { ...rider.common, maxDiscountBp: rider.maxBp, maxDiscountPerRideMinor: rider.perUnit },
        DRIVER_BOOST: { ...driver.common, maxBoostMinor: driver.perUnit },
        COURIER_BOOST: { ...courier.common, maxBoostMinor: courier.perUnit },
      },
      pacing: { dayBudgetShareBp: share as number, maxDayBudgetMinor: Number(draft.maxDayBudget), hourlyCurveBp: curve, minScaleBp: minScale as number },
    },
  };
}

export interface PreviewRow {
  readonly state: ControllerZoneState;
  readonly current: number | null;
  readonly draft: number;
}

/** The windows per state under the draft and under the policy in force, side by side (null = nothing in force to compare with). */
export function previewRows(draft: CockpitPreview, current: CockpitPreview | null): readonly PreviewRow[] {
  return (['SUPPLY_SHORT', 'DEMAND_SHORT', 'BALANCED', 'QUIET', 'UNKNOWN'] as const).map((state) => ({ state, current: current === null ? null : current.totals.states[state], draft: draft.totals.states[state] }));
}

export interface LeverPreviewRow {
  readonly lever: ControllerLever;
  readonly current: number | null;
  readonly draft: number;
}

export function previewLeverRows(draft: CockpitPreview, current: CockpitPreview | null): readonly LeverPreviewRow[] {
  return (['RIDER_ZONE_DISCOUNT', 'DRIVER_BOOST', 'COURIER_BOOST'] as const).map((lever) => ({ lever, current: current === null ? null : current.totals.leverWindows[lever], draft: draft.totals.leverWindows[lever] }));
}

/** Windows (5 minutes each) as whole minutes. */
export const windowsToMinutes = (windows: number): number => windows * 5;

// ---- results (the measurement, D496) -----------------------------------------------------------------------------------------

/** The market-local calendar day (`YYYY-MM-DD`) an instant falls on. */
export function marketLocalDay(ms: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timeZone ?? 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
  } catch {
    return new Date(ms).toISOString().slice(0, 10);
  }
}

/** The last `count` finished market-local days, newest first: yesterday, the day before ... (today is still filling and is never offered). Steps by CALENDAR day in the zone, never by 24 hours. */
export function recentDays(nowMs: number, timeZone: string | undefined, count: number): readonly string[] {
  const [y, m, d] = marketLocalDay(nowMs, timeZone).split('-').map(Number) as [number, number, number];
  return Array.from({ length: count }, (_, i) => new Date(Date.UTC(y, m - 1, d - 1 - i)).toISOString().slice(0, 10));
}

/** A return in basis points as a percentage with one decimal (`8000` -> `80`, `-1250` -> `-12.5`). */
export function roiPercent(bp: number): string {
  const tenths = Math.round(bp / 10);
  const sign = tenths < 0 ? '-' : '';
  const abs = Math.abs(tenths);
  return `${sign}${Math.floor(abs / 10)}${abs % 10 === 0 ? '' : `.${abs % 10}`}`;
}

export const roiTone = (roiBp: number | null): CockpitTone => (roiBp === null ? 'neutral' : roiBp < 0 ? 'danger' : roiBp === 0 ? 'warning' : 'success');
export const autoStopTone = (status: string): CockpitTone => (status === 'STOPPED' ? 'danger' : status === 'WATCHING' ? 'warning' : status === 'ACTIVE' ? 'success' : 'neutral');

// ---- published version vs the policy in force (review W10K-01) ------------------------------------------------------------------

/** What the policy IN FORCE has that the PUBLISHED version does not: a country's tightening or the shadow floor. The editor never writes these into the market's version; it names them. */
export type EffectiveDifference = { readonly kind: 'field'; readonly key: string } | { readonly kind: 'lever'; readonly lever: ControllerLever } | { readonly kind: 'rules' } | { readonly kind: 'pacing' };

export function effectiveDifferences(published: ControllerPolicyDraft, effective: ControllerPolicyDraft): readonly EffectiveDifference[] {
  const out: EffectiveDifference[] = [];
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  if (published.mode !== effective.mode) out.push({ kind: 'field', key: 'mode' });
  if (published.minShadowDays !== effective.minShadowDays) out.push({ kind: 'field', key: 'minShadowDays' });
  if (published.intervalSeconds !== effective.intervalSeconds) out.push({ kind: 'field', key: 'intervalSeconds' });
  if (published.decisionTtlSeconds !== effective.decisionTtlSeconds) out.push({ kind: 'field', key: 'decisionTtlMinutes' });
  if (published.maxSignalAgeSeconds !== effective.maxSignalAgeSeconds) out.push({ kind: 'field', key: 'maxSignalAgeSeconds' });
  if (!same(published.rules, effective.rules)) out.push({ kind: 'rules' });
  for (const lever of ['RIDER_ZONE_DISCOUNT', 'DRIVER_BOOST', 'COURIER_BOOST'] as const) if (!same(published.levers[lever], effective.levers[lever])) out.push({ kind: 'lever', lever });
  if (!same(published.pacing, effective.pacing)) out.push({ kind: 'pacing' });
  return out;
}
